import { useCallback } from 'react'
import { TicketTypeRow, type TicketTypeRowData } from './TicketTypeRow'

interface Props {
  rows: TicketTypeRowData[]
  onChange: (rows: TicketTypeRowData[]) => void
  error?: string
}

function makeRow(overrides?: Partial<TicketTypeRowData>): TicketTypeRowData {
  return {
    id: Math.random().toString(36).slice(2),
    name: '',
    description: '',
    price: '',
    capacity: '0',
    ...overrides,
  }
}

export function TicketTypeManager({ rows, onChange, error }: Props) {
  const handleChange = useCallback(
    (id: string, field: keyof TicketTypeRowData, value: string) => {
      onChange(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
    },
    [rows, onChange]
  )

  const handleRemove = useCallback(
    (id: string) => {
      if (rows.length <= 1) return
      onChange(rows.filter((r) => r.id !== id))
    },
    [rows, onChange]
  )

  const handleMoveUp = useCallback(
    (id: string) => {
      const idx = rows.findIndex((r) => r.id === id)
      if (idx <= 0) return
      const next = [...rows]
      ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
      onChange(next)
    },
    [rows, onChange]
  )

  const handleMoveDown = useCallback(
    (id: string) => {
      const idx = rows.findIndex((r) => r.id === id)
      if (idx >= rows.length - 1) return
      const next = [...rows]
      ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
      onChange(next)
    },
    [rows, onChange]
  )

  return (
    <div className="flex flex-col gap-3">
      {/* En-têtes */}
      <div className="hidden sm:flex items-center gap-2 px-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div className="w-6" />
        <div className="flex-1">Nom</div>
        <div className="flex-1">Description</div>
        <div className="w-28">Prix</div>
        <div className="w-24">Capacité</div>
        <div className="w-8" />
      </div>

      {/* Lignes */}
      <div className="bg-white rounded-xl border border-gray-200">
        {rows.map((row, i) => (
          <TicketTypeRow
            key={row.id}
            row={row}
            index={i}
            total={rows.length}
            onChange={handleChange}
            onRemove={handleRemove}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
          />
        ))}
      </div>

      {error && (
        <p className="text-xs text-danger-600 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </p>
      )}

      {/* Ajouter */}
      <button
        type="button"
        onClick={() => onChange([...rows, makeRow()])}
        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
        Ajouter un type
      </button>
    </div>
  )
}

export { makeRow }
